import Layout from "../../../components/shared/layout/layout";
import {useEffect, useMemo, useState} from "react";
import {useRouter} from "next/router";
import {deleteCookie, getCookie, getCookies, hasCookie, setCookie} from "cookies-next";
import Seo from "../../../components/shared/seo/seo";

// ---------------------------------------------------------------------------
// Small pricing helpers (mirror of the ones used elsewhere on the shop).
// ---------------------------------------------------------------------------

function getPriceForCountry(countryId, product){
    const entry = product.prices.find((price) => price.countryId == countryId);
    return entry ? entry.value : 0;
}

function packagePrice(pkg){
    if (!Array.isArray(pkg?.productQuantities)) return 0;
    let total = 0;
    for (const pq of pkg.productQuantities) {
        const p = pq?.product;
        if (!p || !Array.isArray(p.prices)) continue;
        const austria = p.prices.find(pr => pr.countryId === 1);
        const value = (austria ?? p.prices[0])?.value ?? 0;
        total += value * (pq.quantity || 0);
    }
    return total;
}

function packageTakesSpace(pkg){
    if (!Array.isArray(pkg?.productQuantities)) return 0;
    let total = 0;
    for (const pq of pkg.productQuantities) {
        total += (pq?.product?.takesSpace ?? 1) * (pq?.quantity || 0);
    }
    return total;
}

async function getProducts(){
    const response = await fetch("https://api.gisperg.wine/api/Product/Headers",{
        method:'GET',
        mode: 'cors'
    });
    return response.json();
}

async function checkDelivery(quantity){
    try {
        const res = await fetch(`https://api.gisperg.wine/api/Delivery/ValidDelivery?quantity=${quantity}`, {
            method: "GET",
            mode: "cors",
        });
        if (res.status === 200) return await res.json();
    } catch (_) { /* fall through */ }
    return false;
}

// ---------------------------------------------------------------------------
// Cart cookie handling
// ---------------------------------------------------------------------------

/** Build the shape we persist in a cart-product-<id> cookie. */
function buildProductEntry(product, activeProduct, count) {
    return {
        id: activeProduct.productId,
        name: `${product.name} ${product.klassifikation ?? ''} ${activeProduct.vintage ? activeProduct.vintage.split("-")[0] : ""}`.trim(),
        capacity: activeProduct.capacity,
        count,
        takesSpace: activeProduct.takesSpace,
        price: getPriceForCountry(1, activeProduct),
        taxRate: product.taxRate,
    };
}

/** cart-package-<headerId> cookie. Includes the contents so kassa can
 *  expand into individual CartItems on checkout. */
function buildPackageEntry(pkg, count) {
    return {
        kind: 'package',
        id: pkg.productHeaderId,
        name: `${pkg.name} ${pkg.klassifikation ?? ''}`.trim(),
        count,
        price: packagePrice(pkg),
        takesSpace: packageTakesSpace(pkg),
        taxRate: pkg.taxRate,
        contents: (pkg.productQuantities ?? []).map(pq => ({
            productId: pq.productId,
            quantity: pq.quantity,
        })),
    };
}

/** Persist or clear a single line's cookie based on the requested count. */
function persistLine(key, entry) {
    if (!entry || entry.count <= 0) {
        if (hasCookie(key)) deleteCookie(key);
    } else {
        setCookie(key, JSON.stringify(entry));
    }
}

/** Read the current cart cookies into a { key: count } map so we can
 *  seed the quick-order quantities with whatever's already in the cart. */
function readCartQuantities() {
    const cookies = getCookies();
    const result = {};
    for (const cookie in cookies) {
        if (!cookie.startsWith("cart-product-") && !cookie.startsWith("cart-package-")) continue;
        try {
            const entry = JSON.parse(decodeURIComponent(cookies[cookie]));
            if (entry?.count > 0) result[cookie] = entry.count;
        } catch (_) { /* skip malformed */ }
    }
    return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function QuickOrder() {
    const router = useRouter();
    const [products, setProducts] = useState(null);
    /** Map<cookieKey, count>. Single source of truth — every +/- click
     *  updates this and immediately writes the matching cookie so the
     *  side cart and the kassa page stay in sync. */
    const [quantities, setQuantities] = useState({});
    const [error, setError] = useState("");
    const [confirming, setConfirming] = useState(false);

    // ------------------------------------------------------------------
    // Load
    // ------------------------------------------------------------------
    useEffect(() => {
        getProducts().then(setProducts).catch(() => setProducts([]));
        setQuantities(readCartQuantities());
    }, []);

    // ------------------------------------------------------------------
    // Build a flat list of pickable lines from the fetched catalogue.
    // Each entry carries the cookie key + the info we need to render
    // and to build the persisted cookie payload.
    // ------------------------------------------------------------------
    const lines = useMemo(() => {
        if (!products) return [];
        const rows = [];
        for (const product of products) {
            if (!product.isVisible) continue;
            if (product.kind === 'Package') {
                const price = packagePrice(product);
                if (price <= 0) continue;
                rows.push({
                    key: `cart-package-${product.productHeaderId}`,
                    label: `${product.name} ${product.klassifikation ?? ''}`.trim(),
                    price,
                    build: (count) => buildPackageEntry(product, count),
                });
                continue;
            }
            for (const activeProduct of product.products ?? []) {
                if (!activeProduct.isInStock || !activeProduct.isVisible) continue;
                const price = getPriceForCountry(1, activeProduct);
                if (price === 0) continue;
                const year = activeProduct.vintage ? activeProduct.vintage.split("-")[0] : '';
                const caption = activeProduct.caption ?? '';
                const capacity = activeProduct.capacity != null ? `${activeProduct.capacity}l` : '';
                const label = `${product.name} ${product.klassifikation ?? ''} ${year} ${caption} ${capacity}`
                    .replace(/\s+/g, ' ').trim();
                rows.push({
                    key: `cart-product-${activeProduct.productId}`,
                    label,
                    price,
                    build: (count) => buildProductEntry(product, activeProduct, count),
                });
            }
        }
        return rows;
    }, [products]);

    // ------------------------------------------------------------------
    // Quantity mutation. Immediately writes the cookie so the side cart
    // reflects the change without a Zur-Kassa round-trip.
    // ------------------------------------------------------------------
    const setQty = (line, next) => {
        const value = Math.max(0, Math.min(999, next | 0));
        setQuantities((prev) => {
            const merged = { ...prev, [line.key]: value };
            if (value <= 0) delete merged[line.key];
            return merged;
        });
        persistLine(line.key, value > 0 ? line.build(value) : null);
        // Ping the layout slideout so it refetches next time it opens.
        // (Cheap: it re-reads on open anyway; this is belt-and-braces.)
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('gisperg-cart-updated'));
        }
    };

    // ------------------------------------------------------------------
    // Cart summary derived from state
    // ------------------------------------------------------------------
    const cartLines = useMemo(() => {
        return lines
            .filter((l) => (quantities[l.key] ?? 0) > 0)
            .map((l) => ({ ...l, count: quantities[l.key] }));
    }, [lines, quantities]);

    const cartTotal = cartLines.reduce((sum, l) => sum + l.count * l.price, 0);

    // ------------------------------------------------------------------
    // Checkout
    // ------------------------------------------------------------------
    async function toKassa() {
        // Recompute totalCount from state so we don't depend on DOM refs.
        let totalCount = 0;
        if (products) {
            for (const product of products) {
                if (product.kind === 'Package') {
                    const c = quantities[`cart-package-${product.productHeaderId}`] ?? 0;
                    if (c > 0) totalCount += c * packageTakesSpace(product);
                    continue;
                }
                for (const active of product.products ?? []) {
                    const c = quantities[`cart-product-${active.productId}`] ?? 0;
                    if (c > 0) totalCount += c * (active.takesSpace ?? 1);
                }
            }
        }
        if (totalCount === 0) {
            setError("Bitte wählen Sie zuerst mindestens eine Flasche.");
            return;
        }
        setConfirming(true);
        const ok = await checkDelivery(totalCount);
        setConfirming(false);
        if (!ok) {
            const missing = 6 - (totalCount % 6);
            setError(`Die Bestellmenge muss ein Vielfaches von 6 betragen. (Momentan fehlen ${missing} Flasche(n))`);
            return;
        }
        setError("");
        router.push("/weinkauf/shop/kassa");
    }

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <Layout>
            <Seo title="Schnellbestellung" description="Schnellbestellung — direkt Mengen eingeben und bestellen." path="/weinkauf/shop/quickOrder" />
            <style jsx>{`
                .qo-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 320px;
                    gap: 2rem;
                    padding: 0 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                    box-sizing: border-box;
                }
                @media (max-width: 900px) {
                    .qo-grid { grid-template-columns: 1fr; }
                }
                .qo-line {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 0.75rem 0;
                    border-bottom: 1px dotted #c8b9a3;
                }
                .qo-line .label { flex: 1; min-width: 0; }
                .qo-line .price {
                    min-width: 80px;
                    text-align: right;
                    font-variant-numeric: tabular-nums;
                }
                .qo-qty {
                    display: inline-flex;
                    align-items: center;
                    border: 1px solid #7C6765;
                    border-radius: 999px;
                    overflow: hidden;
                    user-select: none;
                }
                .qo-qty button {
                    border: 0;
                    background: transparent;
                    width: 34px;
                    height: 34px;
                    cursor: pointer;
                    font-size: 18px;
                    line-height: 1;
                    color: #7C6765;
                }
                .qo-qty button:hover:not(:disabled) { background: #7C676511; }
                .qo-qty button:disabled { color: #ccc; cursor: not-allowed; }
                .qo-qty .count {
                    min-width: 32px;
                    text-align: center;
                    font-variant-numeric: tabular-nums;
                }
                .qo-cart {
                    position: sticky;
                    top: 90px;
                    align-self: start;
                    background: #FFFFFF66;
                    border: 1px solid #c8b9a3;
                    border-radius: 8px;
                    padding: 1.25rem;
                }
                @media (max-width: 900px) {
                    .qo-cart { position: static; }
                }
                .qo-cart h3 { margin-top: 0; }
                .qo-cart-line {
                    display: flex;
                    justify-content: space-between;
                    gap: 0.75rem;
                    padding: 0.35rem 0;
                    font-size: 15px;
                    border-bottom: 1px dotted #c8b9a3;
                }
                .qo-cart-line:last-of-type { border-bottom: 0; }
                .qo-cart-total {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 2px solid #7C6765;
                    font-weight: 600;
                }
                .qo-empty { color: #7C6765; font-size: 14px; }
            `}</style>
            <section className="background_light no_fill small">
                <h1 style={{textAlign:"center"}}>Schnell<br/>Bestellung</h1>
            </section>
            <section className="background_lighter" style={{padding: "2rem 0"}}>
                <div className="qo-grid">
                    {/* --- Product list --- */}
                    <div>
                        {products == null && <p>Lädt …</p>}
                        {products?.length === 0 && <p>Keine Produkte verfügbar.</p>}
                        {lines.map((line) => {
                            const count = quantities[line.key] ?? 0;
                            return (
                                <div key={line.key} className="qo-line">
                                    <div className="qo-qty" aria-label={`Menge ${line.label}`}>
                                        <button type="button" onClick={() => setQty(line, count - 1)} disabled={count <= 0}>−</button>
                                        <span className="count narrow">{count}</span>
                                        <button type="button" onClick={() => setQty(line, count + 1)}>+</button>
                                    </div>
                                    <span className="label narrow uppercase size-16">{line.label}</span>
                                    <span className="price narrow uppercase size-16">
                                        {(Math.round(line.price * 100) / 100).toFixed(2)} €
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* --- Live cart summary --- */}
                    <aside className="qo-cart">
                        <h3 className="uppercase" style={{margin: 0, marginBottom: "0.75rem"}}>Warenkorb</h3>
                        {cartLines.length === 0 ? (
                            <p className="qo-empty">Noch keine Flaschen ausgewählt.</p>
                        ) : (
                            <>
                                {cartLines.map((l) => (
                                    <div key={l.key} className="qo-cart-line">
                                        <span>{l.count} × {l.label}</span>
                                        <span style={{whiteSpace: "nowrap"}}>
                                            {(Math.round(l.count * l.price * 100) / 100).toFixed(2)} €
                                        </span>
                                    </div>
                                ))}
                                <div className="qo-cart-total">
                                    <span>Gesamt</span>
                                    <span>{(Math.round(cartTotal * 100) / 100).toFixed(2)} €</span>
                                </div>
                            </>
                        )}
                        {error !== "" && (
                            <p style={{color: "red", fontSize: 14, marginTop: "1rem"}}>{error}</p>
                        )}
                        <p className="button primary x2" style={{marginTop: "1rem"}} onClick={toKassa}>
                            {confirming ? "Prüfe …" : "Zur Kassa"}
                        </p>
                    </aside>
                </div>
            </section>
        </Layout>
    );
}
